// src/components/DataFilesContext.tsx
// VERSIONE CORRETTA - Risolve il problema di caricamento dati

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { StandardTrade, CustomColumn } from '../types/database';
import DataTransformationBridge from '../services/DataTransformationBridge';
import { ColumnAnalysisResult } from '../services/EnhancedColumnIdentifier';
import { SupabaseDataService } from '../services/SupabaseDataService';
import { UIStateService } from '../services/UIStateService';
import { isSupabaseConfigured } from '../config/supabase';
import { DEMO_TRADES_JAN, DEMO_TRADES_FEB } from '../data/demoTrades';
import { SupabaseAuthService } from '../services/SupabaseAuthService';
import { extractUserTajirPlan } from '../services/TajirPlanService';
import { getDataImportPolicy, SubscriptionTier } from '../services/SubscriptionService';

/**
 * StandardFile interface
 */
export interface StandardFile {
  id: string;
  name: string;
  trades: StandardTrade[];
  isDemo?: boolean; // true for the built-in demo file shown to new users
  initialCapital?: number; // Capitale iniziale del conto per questo file
  metadata: {
    dateRange: {
      from: string;
      to: string;
    };
    uploadedAt: string;
    platform?: string | null;
    confidence?: number;
    totalTrades: number;
    unmappedColumns?: string[];
    initialCapital?: number; // Anche in metadata per compatibilità Supabase
  };
}

/** @deprecated Use StandardFile instead */
export type DataFile = StandardFile;

/**
 * ImportResult interface
 */
/** Sentinel for "the plan says no", so callers can show a prompt not a string. */
export const PLAN_LIMIT_FILES = 'PLAN_LIMIT_FILES';

export interface PlanLimitInfo {
  kind: 'files';
  used: number;
  max: number;
  plan: string;
}

export interface ImportResult {
  success: boolean;
  /** Present when the import was refused by the plan, not by the data. */
  limit?: PlanLimitInfo;
  file?: StandardFile;
  errors: string[];
  warnings: string[];
  analysisResult?: ColumnAnalysisResult;
}

/**
 * Context Type
 */
interface DataFilesContextType {
  // State
  standardFiles: StandardFile[];
  activeFileId: string | null;
  isLoading: boolean;
  /**
   * Set when the server could not be reached, cleared on a successful read.
   * The product is a web app: without a server there is nothing to show, and
   * saying so beats guessing that the account is empty.
   */
  loadError: string | null;

  // File Actions
  addFile: (name: string, csvData: { headers: string[], rows: string[][] }, options?: { allowEmpty?: boolean; initialCapital?: number }) => Promise<ImportResult>;
  removeFile: (fileId: string) => Promise<void>;
  setActiveFile: (fileId: string | null) => void;
  clearAllFiles: () => void;
  refreshData: () => Promise<void>;
  updateFileCapital: (fileId: string, capital: number) => Promise<void>;
  updateFileName: (fileId: string, name: string) => Promise<void>;

  // Trade CRUD
  addTrade: (fileId: string, trade: Partial<StandardTrade>) => Promise<{ success: boolean; error?: string }>;
  updateTrade: (fileId: string, tradeId: string, updates: Partial<StandardTrade>, options?: { skipRefresh?: boolean }) => Promise<{ success: boolean; error?: string }>;
  deleteTrades: (fileId: string, tradeIds: string[]) => Promise<{ success: boolean; error?: string }>;

  // Column CRUD
  addColumn: (fileId: string, column: Omit<CustomColumn, 'id'>) => Promise<{ success: boolean; error?: string }>;
  updateColumn: (fileId: string, columnId: string, updates: Partial<CustomColumn>) => Promise<{ success: boolean; error?: string }>;
  deleteColumn: (fileId: string, columnId: string) => Promise<{ success: boolean; error?: string }>;

  // Queries
  getAllTrades: () => StandardTrade[];
  getActiveFile: () => StandardFile | null;
  getFileById: (fileId: string) => StandardFile | null;
  getFileKPIs: (fileId: string) => any;
  getAllKPIs: () => any;

  // Demo
  isDemoActive: boolean;
  removeDemo: () => void;
  restoreDemo: () => void;
}

// Create Context
const DataFilesContext = createContext<DataFilesContextType | undefined>(undefined);

// Storage key
const STORAGE_KEY = 'tradingspace_files_v2';
const DEMO_REMOVED_KEY = (user: string | null) => `demo_removed_${user || 'anon'}`;
const ADMIN_EMAILS = ['addoumzaid@gmail.com'];

/** Map a raw DemoTrade array to StandardTrade-compatible objects */
const mapDemoTrades = (raw: typeof DEMO_TRADES_JAN) =>
  raw.map(t => ({
    id: t.id,
    date: t.openTime.split('T')[0],
    symbol: t.symbol,
    type: t.direction as 'LONG' | 'SHORT',
    // `profit` and `netProfit` used to disagree: the first was gross, the second
    // net of commission. Nothing downstream could tell which a given screen
    // meant, so the dashboard toolbar showed one figure and the equity widget
    // another for the same file — €32 against €113.50, the larger one labelled
    // NET. Both now carry the net result, which is what a broker export's
    // profit column reports; `commission` is kept for display.
    profit: t.profit + t.commission,
    netProfit: t.profit + t.commission,
    commission: t.commission,
    entryPrice: t.entryPrice,
    exitPrice: t.exitPrice,
    quantity: t.volume,
    // Date and time apart, time-only — the shape SupabaseDataService gives
    // real trades. As full timestamps, every widget that reads
    // `${date}T${entryTime}` got an invalid date and dropped the trade: the
    // Streak Tracker on the demo file said "last 0 trades".
    entryTime: t.openTime.split('T')[1]?.replace('Z', ''),
    exitDate: t.closeTime.split('T')[0],
    exitTime: t.closeTime.split('T')[1]?.replace('Z', ''),
  }));

/** Build two realistic demo StandardFiles (January and February 2026) */
const buildDemoFiles = (): StandardFile[] => {
  const janTrades = mapDemoTrades(DEMO_TRADES_JAN);
  const febTrades = mapDemoTrades(DEMO_TRADES_FEB);
  return [
    {
      id: 'demo_file_jan',
      name: 'MT4_January_2026',
      isDemo: true,
      trades: janTrades as any,
      metadata: {
        dateRange: { from: '2026-01-02', to: '2026-01-30' },
        uploadedAt: new Date().toISOString(),
        platform: 'MetaTrader 4',
        totalTrades: janTrades.length,
      },
    },
    {
      id: 'demo_file_feb',
      name: 'MT4_February_2026',
      isDemo: true,
      trades: febTrades as any,
      metadata: {
        dateRange: { from: '2026-02-02', to: '2026-02-28' },
        uploadedAt: new Date().toISOString(),
        platform: 'MetaTrader 4',
        totalTrades: febTrades.length,
      },
    },
  ];
};

/**
 * Provider Component - CORRETTA (con multi-user support)
 */
export const DataFilesProvider: React.FC<{
  children: React.ReactNode;
  currentUser: string | null;
}> = ({ children, currentUser }) => {
  const [standardFiles, setStandardFiles] = useState<StandardFile[]>([]);
  const [activeFileId, setActiveFileIdState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasInitialized = useRef(false);
  const lastInitializedUser = useRef<string | null>(null);

  const getCurrentPlan = useCallback(async (): Promise<SubscriptionTier> => {
    const user = await SupabaseAuthService.getCurrentUser();
    if (user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase())) {
      return 'advanced';
    }
    return extractUserTajirPlan(user);
  }, []);

  // Load from localStorage on mount - VERSIONE CORRETTA con user-specific storage
  useEffect(() => {
    // 🔧 FIX: Evita re-inizializzazioni se l'utente non è cambiato
    if (lastInitializedUser.current === currentUser && hasInitialized.current) {
      console.log('⚡ Skip re-initialization - same user');
      return;
    }

    // Reset quando cambia utente
    hasInitialized.current = false;
    lastInitializedUser.current = currentUser;
    
    const initializeData = async () => {
      setIsLoading(true);
      console.log('🔄 DataFilesContext: Inizializzazione...');
      console.log(`   User: ${currentUser || 'anonymous'}`);
      console.log(`   Supabase: ${isSupabaseConfigured() ? '✅ Enabled' : '❌ Disabled'}`);
      
      try {
        // Se Supabase è configurato, carica da cloud
        if (isSupabaseConfigured() && currentUser) {
          console.log('☁️ Caricamento da Supabase...');
          // Asked in the form that distinguishes "no files" from "no answer".
          // The plain read returns [] for both, and this branch treats [] as a
          // brand-new account: it installs the demo files and restarts
          // onboarding. A single timeout on `trading_files` was therefore
          // enough to make a full account look new — which is exactly what was
          // reported, twice.
          const result = await SupabaseDataService.getUserFilesWithStatus();

          if (!result.ok) {
            console.error('❌ Supabase non raggiungibile:', result.error);
            setStandardFiles([]);
            setActiveFileIdState(null);
            setLoadError(result.error);
            return;
          }
          setLoadError(null);

          const supabaseFiles = result.files;

          if (supabaseFiles.length > 0) {
            console.log(`✅ Caricati ${supabaseFiles.length} file da Supabase`);
            setStandardFiles(supabaseFiles);
            setActiveFileIdState(null); // Don't auto-open the first file

            // 🔄 SYNC: Salva anche in localStorage per StandardTradingDataManager
            const { StandardTradingDataManager } = await import('../utils/standardTradingDataManager');
            StandardTradingDataManager.syncFromSupabase(supabaseFiles);
          } else {
            console.log('⚠️ Nessun file trovato su Supabase — carico demo di default');
            if (localStorage.getItem(DEMO_REMOVED_KEY(currentUser)) !== 'true') {
              const demoFiles = buildDemoFiles();
              setStandardFiles(demoFiles);
              setActiveFileIdState(demoFiles[0].id);
            } else {
              setStandardFiles([]);
              setActiveFileIdState(null);
            }
          }
        } else {
          // Fallback a localStorage
          console.log('💾 Caricamento da localStorage...');
          const storageKey = currentUser ? `tradingspace_data_${currentUser}` : STORAGE_KEY;
          const stored = localStorage.getItem(storageKey);
          
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed.files && Array.isArray(parsed.files) && parsed.files.length > 0) {
              console.log(`✅ Caricati ${parsed.files.length} file per ${currentUser}`);
            console.log(`   - Primo file: ${parsed.files[0].name} (${parsed.files[0].trades.length} trades)`);
            
            // Set dei dati
            setStandardFiles(parsed.files);
            setActiveFileIdState(parsed.activeFileId || null);
            
            // Verifica struttura dati
            if (parsed.files[0]?.trades?.[0]) {
              const sampleTrade = parsed.files[0].trades[0];
              console.log('📊 Struttura trade esempio:', {
                id: sampleTrade.id,
                date: sampleTrade.date,
                symbol: sampleTrade.symbol,
                profit: sampleTrade.profit
              });
            }
          } else {
            console.log('⚠️ Nessun file trovato per questo utente — carico demo di default');
            if (localStorage.getItem(DEMO_REMOVED_KEY(currentUser)) !== 'true') {
              const demoFiles = buildDemoFiles();
              setStandardFiles(demoFiles);
              setActiveFileIdState(demoFiles[0].id);
            } else {
              setStandardFiles([]);
              setActiveFileIdState(null);
            }
          }
          } else {
            console.log('⚠️ localStorage vuoto per questo utente — carico demo di default');
            if (localStorage.getItem(DEMO_REMOVED_KEY(currentUser)) !== 'true') {
              const demoFiles = buildDemoFiles();
              setStandardFiles(demoFiles);
              setActiveFileIdState(demoFiles[0].id);
            } else {
              setStandardFiles([]);
              setActiveFileIdState(null);
            }
          }
        }
      } catch (error) {
        console.error('❌ Errore caricamento dati:', error);
        setStandardFiles([]);
        setActiveFileIdState(null);
      } finally {
        setIsLoading(false);
        console.log('✅ Inizializzazione completata');
        hasInitialized.current = true;
      }
    };

    // Inizializza con un piccolo delay per assicurarsi che il DOM sia pronto
    setTimeout(initializeData, 0);
  }, [currentUser]);

  // Save to localStorage on change - VERSIONE OTTIMIZZATA con user-specific storage
  useEffect(() => {
    // Non salvare durante il loading iniziale
    if (isLoading || !hasInitialized.current || !currentUser) {
      return;
    }

    // When Supabase is the source of truth this mirror is never read.
    //
    // The load path above takes the cloud branch for any signed-in user, and a
    // failed read now sets `loadError` and stops — it does not fall back here.
    // So writing every file with every trade into one blob, on every change,
    // bought nothing and cost a great deal: a JSON.stringify of the whole
    // dataset blocking the main thread, against a ~5 MB budget that a few
    // hundred trades exhaust. That is the "app is slow to show data" and the
    // quota errors both.
    //
    // The note that used to be here warned that two attempts to be cleverer had
    // done real damage — a trades-stripped copy made a full account render as
    // empty on a Supabase timeout, and refusing the copy sent the app down the
    // no-data path into demo files and onboarding. Both were failures of a
    // *partial* cache being trusted. Not writing one at all cannot be trusted
    // wrongly, and the timeout case is now handled by `loadError` instead.
    //
    // The anonymous and Supabase-less paths still use localStorage, and still
    // write here, because for them it is the only copy there is.
    if (isSupabaseConfigured() && currentUser) {
      // Clear any blob left by an earlier version, so it can never be picked up
      // later and served as current.
      try {
        localStorage.removeItem(`tradingspace_data_${currentUser}`);
      } catch {
        /* nothing to clean up */
      }
      return;
    }

    try {
      const storageKey = currentUser ? `tradingspace_data_${currentUser}` : STORAGE_KEY;
      const dataToSave = {
        files: standardFiles,
        activeFileId: activeFileId,
        version: '2.0',
        timestamp: Date.now()
      };
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      console.log(`💾 Salvati ${standardFiles.length} file per ${currentUser || 'anonymous'}`);
    } catch (error) {
      console.error('❌ Errore salvataggio localStorage:', error);
    }
  }, [standardFiles, activeFileId, isLoading, currentUser]);

  /**
   * Add new file using DataTransformationBridge
   */
  const addFile = useCallback(async (
    name: string,
    csvData: { headers: string[], rows: string[][] },
    options?: { allowEmpty?: boolean; initialCapital?: number }
  ): Promise<ImportResult> => {
    console.log('📥 DataFilesContext: Avvio import...');
    console.log(`   File: ${name}`);
    console.log(`   Headers: ${csvData.headers.length}`);
    console.log(`   Rows: ${csvData.rows.length}`);
    console.log(`   Supabase: ${isSupabaseConfigured() ? '✅' : '❌'}`);

    try {
      // Use DataTransformationBridge (pass allowEmpty option)
      const transformResult = DataTransformationBridge.transform(csvData, options);

      // Check critical errors
      if (transformResult.errors.length > 0) {
        console.error('❌ Errori durante trasformazione:', transformResult.errors);
        return {
          success: false,
          errors: transformResult.errors,
          warnings: transformResult.warnings,
          analysisResult: transformResult.analysisResult
        };
      }

      // Check valid trades (allow empty if explicitly requested)
      if (transformResult.trades.length === 0 && !options?.allowEmpty) {
        console.error('❌ Nessun trade valido trovato');
        return {
          success: false,
          errors: ['Nessun trade valido trovato nel CSV. Verifica che il file contenga almeno: date, symbol, profit'],
          warnings: transformResult.warnings,
          analysisResult: transformResult.analysisResult
        };
      }

      // If empty file is allowed, create it with empty trades
      if (transformResult.trades.length === 0 && options?.allowEmpty) {
        console.log('✅ Creazione file vuoto (allowEmpty=true)');
      }

      const currentPlan = await getCurrentPlan();
      const importPolicy = getDataImportPolicy(currentPlan);
      const realFiles = standardFiles.filter(file => !file.isDemo);

      if (transformResult.trades.length > importPolicy.maxTradesPerFile) {
        return {
          success: false,
          errors: [
            `Il tuo piano ${currentPlan} consente massimo ${importPolicy.maxTradesPerFile} trade per file. ` +
            `Questo file contiene ${transformResult.trades.length} trade.`
          ],
          warnings: transformResult.warnings,
          analysisResult: transformResult.analysisResult
        };
      }

      // Calculate date range
      const dateRange = calculateDateRange(transformResult.trades);

      // Se Supabase è configurato, salva su cloud
      if (isSupabaseConfigured() && currentUser) {
        console.log('☁️ Salvataggio su Supabase...');

        // Check if file with same name already exists
        const fileCheck = await SupabaseDataService.findFileByName(name);

        if (fileCheck.exists && fileCheck.fileId) {
          console.log('⚠️ File già esistente:', name);
          console.log(`   - File ID: ${fileCheck.fileId}`);
          console.log(`   - Trades esistenti: ${fileCheck.file?.trades.length || 0}`);
          console.log(`   - Nuove trades: ${transformResult.trades.length}`);

          const existingMergeKeys = new Set((fileCheck.file?.trades || []).map(buildTradeMergeKey));
          const uniqueIncomingCount = transformResult.trades.filter(trade => !existingMergeKeys.has(buildTradeMergeKey(trade))).length;
          const projectedTrades = (fileCheck.file?.trades.length || 0) + uniqueIncomingCount;
          if (projectedTrades > importPolicy.maxTradesPerFile) {
            return {
              success: false,
              errors: [
                `Il merge supererebbe il limite del tuo piano: ${projectedTrades}/${importPolicy.maxTradesPerFile} trade nel file.`
              ],
              warnings: transformResult.warnings,
              analysisResult: transformResult.analysisResult
            };
          }

          // 🔄 AUTO-MERGE: Aggiorna automaticamente il file aggiungendo solo i nuovi trade (no duplicati)
          console.log('🔄 Auto-merge: aggiunta automatica nuove trade...');
          const mergeResult = await SupabaseDataService.updateFileWithNewTrades(
            fileCheck.fileId,
            transformResult.trades
          );

          if (!mergeResult.success) {
            console.error('❌ Errore merge:', mergeResult.error);
            return {
              success: false,
              errors: [mergeResult.error || 'Errore merge trade'],
              warnings: transformResult.warnings,
              analysisResult: transformResult.analysisResult
            };
          }

          // Ricarica i file da Supabase per aggiornare lo stato
          const updatedFiles = await SupabaseDataService.getUserFiles();
          localStorage.setItem(DEMO_REMOVED_KEY(currentUser), 'true');
          setStandardFiles(updatedFiles);

          // 🔄 SYNC: Salva anche in localStorage
          const { StandardTradingDataManager: SDM } = await import('../utils/standardTradingDataManager');
          SDM.syncFromSupabase(updatedFiles);

          // Trova il file aggiornato e imposta come attivo
          const updatedFile = updatedFiles.find(f => f.id === fileCheck.fileId);
          if (updatedFile) {
            setActiveFileIdState(updatedFile.id);
          }

          console.log(`✅ File aggiornato automaticamente con merge:`, name);
          console.log(`   - ID: ${fileCheck.fileId}`);
          console.log(`   - Trade aggiunte: ${mergeResult.addedCount}`);
          console.log(`   - Totale trade: ${updatedFile?.trades.length}`);

          return {
            success: true,
            file: updatedFile,
            errors: [],
            warnings: [
              ...transformResult.warnings,
              `🔄 File aggiornato: aggiunti ${mergeResult.addedCount} nuovi trade (duplicati evitati automaticamente)`
            ],
            analysisResult: transformResult.analysisResult
          };
        }

        // Upload new file (either no existing file, or user chose to create new)
        if (realFiles.length >= importPolicy.maxActiveFiles) {
          // A machine-readable marker rather than a sentence. The caller turns
          // this into the upgrade prompt: the person who hit it is trying to
          // add a second trading account, and "Limite file raggiunto per il
          // piano free: 1/1 file attivi" answers a question they did not ask.
          return {
            success: false,
            errors: [PLAN_LIMIT_FILES],
            limit: { kind: 'files', used: realFiles.length, max: importPolicy.maxActiveFiles, plan: currentPlan },
            warnings: transformResult.warnings,
            analysisResult: transformResult.analysisResult
          };
        }

        const uploadResult = await SupabaseDataService.uploadFile(name, transformResult.trades);

        if (!uploadResult.success) {
          console.error('❌ Errore upload Supabase:', uploadResult.error);
          return {
            success: false,
            errors: [uploadResult.error || 'Errore upload Supabase'],
            warnings: transformResult.warnings,
            analysisResult: transformResult.analysisResult
          };
        }

        // Ricarica i file da Supabase per aggiornare lo stato
        const updatedFiles = await SupabaseDataService.getUserFiles();
        // Mark demo as removed when user uploads real data
        localStorage.setItem(DEMO_REMOVED_KEY(currentUser), 'true');
        setStandardFiles(updatedFiles);

        // 🔄 SYNC: Salva anche in localStorage
        const { StandardTradingDataManager: SDM } = await import('../utils/standardTradingDataManager');
        SDM.syncFromSupabase(updatedFiles);

        // Trova il file appena caricato e imposta come attivo
        const uploadedFile = updatedFiles.find(f => f.name === name);
        if (uploadedFile) {
          setActiveFileIdState(uploadedFile.id);
        }

        console.log(`✅ File salvato su Supabase: ${name}`);
        console.log(`   - ID: ${uploadedFile?.id}`);
        console.log(`   - Trades: ${transformResult.trades.length}`);

        return {
          success: true,
          file: uploadedFile,
          errors: [],
          warnings: transformResult.warnings,
          analysisResult: transformResult.analysisResult
        };
      } else {
        // Fallback a localStorage
        console.log('💾 Salvataggio in localStorage...');

        // ── DEDUPLICATION: check if a file with the same name already exists ──
        const existingFile = standardFiles.find(f => f.name === name && !f.isDemo);

        if (existingFile) {
          console.log(`🔄 File già esistente in localStorage: "${name}" (${existingFile.trades.length} trade)`);

          // Build fingerprint set from existing trades
          const existingFingerprints = new Set(
            existingFile.trades.map(tradeFingerprint)
          );

          // Keep only genuinely new trades
          const newTrades = transformResult.trades.filter(
            t => !existingFingerprints.has(tradeFingerprint(t))
          );

          console.log(`   - Nuovi trade: ${newTrades.length} / ${transformResult.trades.length}`);

          if (newTrades.length === 0) {
            console.log('ℹ️ Nessun nuovo trade — file già aggiornato');
            setActiveFileIdState(existingFile.id);
            return {
              success: true,
              file: existingFile,
              errors: [],
              warnings: [...transformResult.warnings, 'Nessun nuovo trade trovato — il file è già aggiornato'],
              analysisResult: transformResult.analysisResult
            };
          }

          const mergedTrades = [...existingFile.trades, ...newTrades];
          if (mergedTrades.length > importPolicy.maxTradesPerFile) {
            return {
              success: false,
              errors: [
                `Il merge supererebbe il limite del tuo piano: ${mergedTrades.length}/${importPolicy.maxTradesPerFile} trade nel file.`
              ],
              warnings: transformResult.warnings,
              analysisResult: transformResult.analysisResult
            };
          }

          const mergedDateRange = calculateDateRange(mergedTrades);

          const updatedFile: StandardFile = {
            ...existingFile,
            trades: mergedTrades,
            metadata: {
              ...existingFile.metadata,
              totalTrades: mergedTrades.length,
              dateRange: mergedDateRange,
              uploadedAt: new Date().toISOString(),
            }
          };

          localStorage.setItem(DEMO_REMOVED_KEY(currentUser), 'true');
          setStandardFiles(prev =>
            prev.map(f => f.id === existingFile.id ? updatedFile : f)
          );
          setActiveFileIdState(existingFile.id);

          console.log(`✅ File aggiornato con merge: aggiunti ${newTrades.length} nuovi trade`);

          return {
            success: true,
            file: updatedFile,
            errors: [],
            warnings: [
              ...transformResult.warnings,
              `🔄 File aggiornato: aggiunti ${newTrades.length} nuovi trade (duplicati evitati automaticamente)`
            ],
            analysisResult: transformResult.analysisResult
          };
        }

        // ── NEW FILE ───────────────────────────────────────────────────────────
        if (realFiles.length >= importPolicy.maxActiveFiles) {
          // A machine-readable marker rather than a sentence. The caller turns
          // this into the upgrade prompt: the person who hit it is trying to
          // add a second trading account, and "Limite file raggiunto per il
          // piano free: 1/1 file attivi" answers a question they did not ask.
          return {
            success: false,
            errors: [PLAN_LIMIT_FILES],
            limit: { kind: 'files', used: realFiles.length, max: importPolicy.maxActiveFiles, plan: currentPlan },
            warnings: transformResult.warnings,
            analysisResult: transformResult.analysisResult
          };
        }

        const newFile: StandardFile = {
          id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name,
          trades: transformResult.trades,
          initialCapital: options?.initialCapital,
          metadata: {
            dateRange,
            uploadedAt: new Date().toISOString(),
            platform: transformResult.analysisResult.detectedPlatform,
            confidence: transformResult.analysisResult.confidence,
            totalTrades: transformResult.trades.length,
            unmappedColumns: transformResult.analysisResult.unmappedColumns,
            initialCapital: options?.initialCapital
          }
        };

        // Add file to state (remove demo file if present)
        localStorage.setItem(DEMO_REMOVED_KEY(currentUser), 'true');
        setStandardFiles(prev => [...prev.filter(f => f.id !== 'demo_file_jan' && f.id !== 'demo_file_feb'), newFile]);

        // Set as active
        setActiveFileIdState(newFile.id);

        console.log(`✅ File importato con successo:`);
        console.log(`   - ID: ${newFile.id}`);
        console.log(`   - Trades: ${newFile.metadata.totalTrades}`);

        return {
          success: true,
          file: newFile,
          errors: [],
          warnings: transformResult.warnings,
          analysisResult: transformResult.analysisResult
        };
      }

    } catch (error: any) {
      console.error('❌ Errore fatale durante import:', error);
      return {
        success: false,
        errors: [`Errore fatale: ${error.message}`],
        warnings: []
      };
    }
  }, [currentUser, getCurrentPlan, standardFiles]);

  /**
   * Remove file by ID
   */
  const removeFile = useCallback(async (fileId: string) => {
    console.log(`🗑️ Rimozione file: ${fileId}`);

    // Demo files live only in memory — never in Supabase.
    // Deleting them just removes them from state and sets the flag so they
    // don't reappear on the next login/refresh.
    const fileToRemove = standardFiles.find(f => f.id === fileId);
    if (fileToRemove?.isDemo || fileId === 'demo_file_jan' || fileId === 'demo_file_feb') {
      // Remove the one that was asked for. This used to drop *both* demo files
      // and set the "never show the demo again" flag, so deleting January also
      // deleted February and there was no way back — reported exactly that way.
      // Deleting one row of sample data is not the same gesture as dismissing
      // the whole demo; that one is the banner's X, which calls removeDemo().
      console.log('🔧 File demo rilevato — rimozione solo da stato locale');
      setStandardFiles(prev => {
        const next = prev.filter(f => f.id !== fileId);
        // Only when the last one goes does the demo count as dismissed,
        // otherwise it would come back on the next load.
        const demoLeft = next.some(f => f.id === 'demo_file_jan' || f.id === 'demo_file_feb');
        if (!demoLeft) localStorage.setItem(DEMO_REMOVED_KEY(currentUser), 'true');
        return next;
      });
      setActiveFileIdState(prev => (prev === fileId ? null : prev));
      return;
    }

    // Se Supabase è configurato, elimina da cloud
    if (isSupabaseConfigured() && currentUser) {
      console.log('☁️ Eliminazione da Supabase...');
      await SupabaseDataService.deleteFile(fileId);

      // 🔧 FIX: Aspetta che la cache sia invalidata (deleteFile invalida la cache)
      await new Promise(resolve => setTimeout(resolve, 100));

      // Ricarica i file (cache è stata invalidata in deleteFile)
      const updatedFiles = await SupabaseDataService.getUserFiles();
      setStandardFiles(updatedFiles);
      console.log(`✅ File rimosso da Supabase. Rimangono ${updatedFiles.length} file`);
    } else {
      // Fallback localStorage
      setStandardFiles(prev => {
        const filtered = prev.filter(f => f.id !== fileId);
        console.log(`✅ File rimosso. Rimangono ${filtered.length} file`);
        return filtered;
      });
    }

    // Reset active file if removed
    if (activeFileId === fileId) {
      setActiveFileIdState(null);
    }
  }, [activeFileId, currentUser, standardFiles]);

  /**
   * Set active file
   */
  const setActiveFile = useCallback((fileId: string | null) => {
    console.log('🔄 setActiveFile chiamato con:', fileId);
    console.log('📊 File disponibili:', standardFiles.map(f => ({ id: f.id, name: f.name, trades: f.trades.length })));

    if (fileId === null || fileId === 'all') {
      console.log('📊 Visualizzazione: Tutti i file');
      setActiveFileIdState(null);
      // Salva anche nel UIStateService
      UIStateService.updateActiveFile(null, currentUser || undefined);
      return;
    }

    const file = standardFiles.find(f => f.id === fileId);
    if (file) {
      console.log(`📊 File attivo: ${file.name} (${file.metadata.totalTrades} trade)`);
      setActiveFileIdState(fileId);
      // Salva anche nel UIStateService
      UIStateService.updateActiveFile(fileId, currentUser || undefined);
    } else {
      console.warn(`⚠️ File ${fileId} non trovato, resetting to all`);
      setActiveFileIdState(null);
      UIStateService.updateActiveFile(null, currentUser || undefined);
    }
  }, [standardFiles, currentUser]);

  // Debug effect per tracciare cambiamenti
  useEffect(() => {
    console.log('🔄 Active file changed:', activeFileId);
    console.log('📊 Available files:', standardFiles.map(f => ({ id: f.id, name: f.name, trades: f.trades.length })));
    
    if (activeFileId && activeFileId !== 'all') {
      const activeFile = standardFiles.find(f => f.id === activeFileId);
      if (activeFile) {
        console.log('✅ Active file found:', activeFile.name, 'with', activeFile.trades.length, 'trades');
      } else {
        console.warn('❌ Active file not found, should reset to all');
      }
    }
  }, [activeFileId, standardFiles]);

  /**
   * Clear all files
   */
  const clearAllFiles = useCallback(() => {
    console.log('🗑️ Cancellazione tutti i file...');
    setStandardFiles([]);
    setActiveFileIdState(null);
    localStorage.removeItem(STORAGE_KEY);
    console.log('✅ Tutti i file cancellati');
  }, []);

  /**
   * Refresh data from storage (Supabase or localStorage)
   */
  const refreshData = useCallback(async () => {
    console.log('🔄 Refresh forzato dei dati...');
    try {
      // Se Supabase è configurato, ricarica da cloud
      if (isSupabaseConfigured() && currentUser) {
        console.log('☁️ Refresh da Supabase...');
        const supabaseFiles = await SupabaseDataService.getUserFiles();

        if (supabaseFiles.length > 0) {
          setStandardFiles(supabaseFiles);
          console.log(`✅ Refresh completato: ${supabaseFiles.length} file da Supabase`);

          // 🔄 SYNC: Salva anche in localStorage
          const { StandardTradingDataManager } = await import('../utils/standardTradingDataManager');
          StandardTradingDataManager.syncFromSupabase(supabaseFiles);
        } else if (localStorage.getItem(DEMO_REMOVED_KEY(currentUser)) !== 'true') {
          // No real files on Supabase and demo not explicitly removed — keep/restore demo files
          setStandardFiles(prev => {
            const hasDemoFiles = prev.some(f => f.id === 'demo_file_jan' || f.id === 'demo_file_feb');
            if (hasDemoFiles) {
              console.log('✅ Refresh: demo files già presenti, nessun cambiamento');
              return prev;
            }
            console.log('✅ Refresh: ripristino file demo');
            return buildDemoFiles();
          });
        } else {
          setStandardFiles([]);
          console.log('✅ Refresh: nessun file (demo rimosso dall\'utente)');
        }
      } else {
        // Fallback a localStorage
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.files && Array.isArray(parsed.files)) {
            setStandardFiles(parsed.files);
            setActiveFileIdState(parsed.activeFileId || null);
            console.log(`✅ Refresh completato: ${parsed.files.length} file da localStorage`);
          }
        }
      }
    } catch (error) {
      console.error('❌ Errore durante refresh:', error);
    }
  }, [currentUser]);

  /**
   * Get all trades from all files - VERSIONE CORRETTA
   */
  const getAllTrades = useCallback((): StandardTrade[] => {
    // Se stiamo ancora caricando, prova a recuperare direttamente dal localStorage
    if (isLoading && standardFiles.length === 0) {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.files && Array.isArray(parsed.files)) {
            const allTrades = parsed.files.flatMap((file: StandardFile) => file.trades || []);
            console.log(`📊 getAllTrades (loading): ${allTrades.length} trade da localStorage`);
            return allTrades;
          }
        }
      } catch (error) {
        console.error('❌ Errore recupero trades durante loading:', error);
      }
    }

    // Normale funzionamento
    return standardFiles.flatMap(file => file.trades || []);
  }, [isLoading, standardFiles]);

  /**
   * Get active file
   */
  const getActiveFile = useCallback((): StandardFile | null => {
    if (!activeFileId) return null;
    return standardFiles.find(f => f.id === activeFileId) || null;
  }, [activeFileId, standardFiles]);

  /**
   * Get file by ID
   */
  const getFileById = useCallback((fileId: string): StandardFile | null => {
    return standardFiles.find(f => f.id === fileId) || null;
  }, [standardFiles]);

  /**
   * Get KPIs for specific file
   */
  const getFileKPIs = useCallback((fileId: string): any => {
    const file = getFileById(fileId);
    if (!file || !file.trades || file.trades.length === 0) return null;

    try {
      const TradingMetricsEngine = require('../services/TradingMetricsEngine').default;
      return TradingMetricsEngine.calculate(file.trades);
    } catch (error) {
      console.error('❌ Errore calcolo KPI:', error);
      return null;
    }
  }, [getFileById]);

  /**
   * Get KPIs for all trades
   */
  const getAllKPIs = useCallback((): any => {
    const allTrades = getAllTrades();
    if (allTrades.length === 0) {
      console.log('⚠️ getAllKPIs: Nessun trade disponibile');
      return null;
    }

    try {
      const TradingMetricsEngine = require('../services/TradingMetricsEngine').default;
      const kpis = TradingMetricsEngine.calculate(allTrades);
      console.log('✅ KPI calcolati per', allTrades.length, 'trades');
      return kpis;
    } catch (error) {
      console.error('❌ Errore calcolo KPI globali:', error);
      return null;
    }
  }, [getAllTrades]);

  // ============================================
  // TRADE CRUD OPERATIONS
  // ============================================

  const addTradeMethod = useCallback(async (
    fileId: string,
    trade: Partial<StandardTrade>
  ): Promise<{ success: boolean; error?: string }> => {
    const { UnifiedDataService } = await import('../services/UnifiedDataService');
    const result = await UnifiedDataService.addTrade(fileId, trade, currentUser);

    if (result.success) {
      await refreshData(); // Reload data after successful operation
    }

    return result;
  }, [currentUser, refreshData]);

  const updateTradeMethod = useCallback(async (
    fileId: string,
    tradeId: string,
    updates: Partial<StandardTrade>,
    options?: { skipRefresh?: boolean }
  ): Promise<{ success: boolean; error?: string }> => {
    const { UnifiedDataService } = await import('../services/UnifiedDataService');
    const result = await UnifiedDataService.updateTrade(fileId, tradeId, updates, currentUser);

    if (result.success && !options?.skipRefresh) {
      await refreshData(); // Reload data after successful operation (unless skipped)
    }

    return result;
  }, [currentUser, refreshData]);

  const deleteTradesMethod = useCallback(async (
    fileId: string,
    tradeIds: string[]
  ): Promise<{ success: boolean; error?: string }> => {
    const { UnifiedDataService } = await import('../services/UnifiedDataService');
    const result = await UnifiedDataService.deleteTrades(fileId, tradeIds, currentUser);

    if (result.success) {
      await refreshData(); // Reload data after successful operation
    }

    return result;
  }, [currentUser, refreshData]);

  // ============================================
  // COLUMN CRUD OPERATIONS
  // ============================================

  const addColumnMethod = useCallback(async (
    fileId: string,
    column: Omit<CustomColumn, 'id'>
  ): Promise<{ success: boolean; error?: string }> => {
    const { UnifiedDataService } = await import('../services/UnifiedDataService');
    const result = await UnifiedDataService.addColumn(fileId, column, currentUser);

    if (result.success) {
      await refreshData(); // Reload data after successful operation
    }

    return result;
  }, [currentUser, refreshData]);

  const updateColumnMethod = useCallback(async (
    fileId: string,
    columnId: string,
    updates: Partial<CustomColumn>
  ): Promise<{ success: boolean; error?: string }> => {
    const { UnifiedDataService } = await import('../services/UnifiedDataService');
    const result = await UnifiedDataService.updateColumn(fileId, columnId, updates, currentUser);

    if (result.success) {
      await refreshData(); // Reload data after successful operation
    }

    return result;
  }, [currentUser, refreshData]);

  const deleteColumnMethod = useCallback(async (
    fileId: string,
    columnId: string
  ): Promise<{ success: boolean; error?: string }> => {
    const { UnifiedDataService } = await import('../services/UnifiedDataService');
    const result = await UnifiedDataService.deleteColumn(fileId, columnId, currentUser);

    if (result.success) {
      await refreshData(); // Reload data after successful operation
    }

    return result;
  }, [currentUser, refreshData]);

  /**
   * Update the initial capital for a file
   */
  const updateFileCapital = useCallback(async (fileId: string, capital: number) => {
    if (isSupabaseConfigured() && currentUser) {
      // Update in Supabase metadata
      await SupabaseDataService.updateFileCapital(fileId, capital);
      const updatedFiles = await SupabaseDataService.getUserFiles();
      setStandardFiles(updatedFiles);
    } else {
      // Update in local state
      setStandardFiles(prev => prev.map(f => {
        if (f.id !== fileId) return f;
        return {
          ...f,
          initialCapital: capital,
          metadata: { ...f.metadata, initialCapital: capital }
        };
      }));
    }
  }, [currentUser]);

  /**
   * Rename a file
   */
  const updateFileName = useCallback(async (fileId: string, name: string) => {
    if (isSupabaseConfigured() && currentUser) {
      await SupabaseDataService.updateFileName(fileId, name);
      const updatedFiles = await SupabaseDataService.getUserFiles();
      setStandardFiles(updatedFiles);
    } else {
      setStandardFiles(prev => prev.map(f => (f.id === fileId ? { ...f, name } : f)));
    }
  }, [currentUser]);

  const isDemoActive = standardFiles.some(f => f.id === 'demo_file_jan' || f.id === 'demo_file_feb');

  /** Dismiss the whole demo — the banner's X. Deliberately removes both files. */
  const removeDemo = useCallback(() => {
    localStorage.setItem(DEMO_REMOVED_KEY(currentUser), 'true');
    setStandardFiles(prev => prev.filter(f => f.id !== 'demo_file_jan' && f.id !== 'demo_file_feb'));
    setActiveFileIdState(null);
  }, [currentUser]);

  /**
   * Put the demo back.
   *
   * "Explore with demo data" used to call `DemoDataService.enableDemoMode`,
   * which writes `demo_mode: true` on a `user_onboarding` row that nothing
   * reads for this purpose — then navigated. It cleared no flag and restored no
   * file, so the button did nothing at all, which is how it was reported.
   *
   * The demo lives in memory and is gated by `demo_removed_<user>` in
   * localStorage, so putting it back means clearing that flag and rebuilding
   * the files. Refuses when the account has real data: silently mixing sample
   * trades into someone's own history would be far worse than a dead button.
   */
  const restoreDemo = useCallback(() => {
    const hasRealFiles = standardFiles.some(f => !f.isDemo && f.id !== 'demo_file_jan' && f.id !== 'demo_file_feb');
    if (hasRealFiles) return;

    localStorage.removeItem(DEMO_REMOVED_KEY(currentUser));
    const demoFiles = buildDemoFiles();
    setStandardFiles(demoFiles);
    setActiveFileIdState(demoFiles[0]?.id ?? null);
  }, [currentUser, standardFiles]);

  // Context value
  const value: DataFilesContextType = {
    standardFiles,
    activeFileId,
    isLoading,
    loadError,
    addFile,
    removeFile,
    setActiveFile,
    clearAllFiles,
    refreshData,
    updateFileCapital,
    updateFileName,
    addTrade: addTradeMethod,
    updateTrade: updateTradeMethod,
    deleteTrades: deleteTradesMethod,
    addColumn: addColumnMethod,
    updateColumn: updateColumnMethod,
    deleteColumn: deleteColumnMethod,
    getAllTrades,
    getActiveFile,
    getFileById,
    getFileKPIs,
    getAllKPIs,
    isDemoActive,
    removeDemo,
    restoreDemo,
  };

  return (
    <DataFilesContext.Provider value={value}>
      {children}
    </DataFilesContext.Provider>
  );
};

/**
 * Hook to use DataFiles context
 */
export const useDataFiles = (): DataFilesContextType => {
  const context = useContext(DataFilesContext);
  if (!context) {
    throw new Error('useDataFiles must be used within DataFilesProvider');
  }
  return context;
};

/**
 * Helper: Build a deduplication fingerprint for a trade.
 * Two trades are considered duplicates if they share the same
 * symbol, entry time (or date as fallback), profit, and type.
 */
function tradeFingerprint(t: StandardTrade): string {
  return `${t.symbol}|${t.date}|${t.entryTime||''}|${t.profit}|${t.type || ''}`;
}

function buildTradeMergeKey(t: StandardTrade): string {
  const normalizePrice = (value: unknown): string => {
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(8) : 'na';
  };

  const normalizeTime = (timeValue: any, dateValue: string): string => {
    if (!timeValue) return dateValue || '';
    const str = String(timeValue);

    if (/^\d{1,2}:\d{2}/.test(str) && !str.includes('T') && !str.includes('/') && !/^\d{4}\./.test(str)) {
      const dateOnly = (dateValue || new Date().toISOString()).split('T')[0];
      const timePart = str.includes('.') ? str.split('.')[0] : str;
      const parsed = new Date(`${dateOnly}T${timePart}Z`);
      return isNaN(parsed.getTime()) ? `${dateOnly}T${timePart}Z` : parsed.toISOString();
    }

    if (/^\d{4}\.\d{2}\.\d{2}[\sT]/.test(str)) {
      const sepIdx = str.search(/[\sT]/);
      const datePart = str.substring(0, sepIdx).replace(/\./g, '-');
      const timePart = str.substring(sepIdx + 1).split('.')[0];
      const parsed = new Date(`${datePart}T${timePart}Z`);
      return isNaN(parsed.getTime()) ? `${datePart}T${timePart}Z` : parsed.toISOString();
    }

    try {
      const parsed = new Date(str);
      return isNaN(parsed.getTime()) ? str : parsed.toISOString();
    } catch {
      return str;
    }
  };

  const baseDate = t.date || new Date().toISOString().split('T')[0];
  return [
    (t.symbol || '').trim().toUpperCase(),
    normalizeTime(t.entryTime || t.date, baseDate),
    normalizePrice(t.entryPrice),
    String(t.type || '').trim().toUpperCase()
  ].join('|');
}

/**
 * Helper: Calculate date range from trades
 */
function calculateDateRange(trades: StandardTrade[]): { from: string, to: string } {
  if (trades.length === 0) {
    return { from: 'N/A', to: 'N/A' };
  }

  const dates = trades
    .map(t => new Date(t.date).getTime())
    .filter(t => !isNaN(t));

  if (dates.length === 0) {
    return { from: 'N/A', to: 'N/A' };
  }

  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));

  return {
    from: minDate.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    }),
    to: maxDate.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    })
  };
}

export default DataFilesContext;
